import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

/**
 * Aba com trilho de ativo.
 *
 * POR QUE ISTO EXISTE
 *
 * O levantamento de 28/08/2026 achou a MESMA aba reimplementada à mão em cinco
 * telas, com três tamanhos de texto diferentes e duas delas com a string de
 * classe copiada literalmente:
 *
 *   navigation/ModuleTabBar.tsx        13px, horizontal
 *   modules/PortfolioModule           13px, horizontal (string idêntica)
 *   modules/KanbanModule/KanbanMetrics 11px, horizontal, sem `cn`
 *   admin/UsersPage                    14px subindo para 16px no `md`
 *   modules/SettingsModule            13px, trilho à ESQUERDA no desktop e
 *                                     embaixo no celular
 *
 * A escada de componentização da skill manda extrair na TERCEIRA repetição.
 * Esta era a quinta, e "aba" nem aparecia na fila de adoção da seção 8, ou seja
 * o backlog também não sabia que o padrão existia.
 *
 * O QUE O PRIMITIVO POSSUI, E O QUE ELE DEIXA PARA A TELA
 *
 * Possui: cor de ativo e de inativo, o trilho, o hover, a tipografia e o
 * `rounded-none` (aba não tem canto arredondado, senão vira pílula).
 *
 * Deixa para a tela: o CONTENTOR. Rolagem lateral com fade, quebra de linha,
 * grupo de ações na ponta direita e a borda da régua são layout da tela, não da
 * aba, e tentar embutir isso aqui foi o que fez o `ui/tabs` do shadcn não servir
 * para o ModuleTabBar em primeiro lugar.
 */

type TabSize = 'md' | 'sm' | 'touch';
type TabOrientation = 'horizontal' | 'vertical';

interface TabProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Aba corrente. Lembre de espelhar em `aria-current="page"` quando a aba é rota. */
  active?: boolean;
  /**
   * `md` = 13px (padrão) · `sm` = 11px, só para aba ANINHADA dentro de painel ·
   * `touch` = 13px com 44px de altura abaixo de `md`.
   *
   * `touch` é opt-in pelo mesmo motivo do `size="touch"` do SegmentedControl:
   * aba que é NAVEGAÇÃO PRIMÁRIA de uma tela alcançável no telefone precisa da
   * escada do dedo, e as outras não devem engordar sem alguém medir. Hoje o caso
   * é Configurações, que está em MODULOS_DE_CELULAR.
   */
  size?: TabSize;
  /** `vertical` põe o trilho à esquerda, para lista de abas em coluna. */
  orientation?: TabOrientation;
  icon?: LucideIcon;
  /** Tamanho do ícone. 16px no horizontal, 18px no vertical, que é mais espaçado. */
  iconSize?: number;
}

const BASE =
  'font-medium rounded-none whitespace-nowrap text-[var(--color-text-muted)] ' +
  'hover:text-[var(--color-text)] hover:bg-[var(--color-bg-hover)]';

const POR_EIXO: Record<TabOrientation, { inativo: string; ativo: string }> = {
  horizontal: {
    inativo: 'border-b-2 border-b-transparent',
    ativo: '!text-[var(--color-primary)] !border-b-[var(--color-primary)]',
  },
  vertical: {
    inativo: 'justify-start w-full gap-2.5 border-l-[3px] border-l-transparent text-[var(--color-text-secondary)]',
    ativo: 'bg-[var(--color-bg-tertiary)] !text-[var(--color-primary)] !border-l-[var(--color-primary)]',
  },
};

const POR_TAMANHO: Record<TabSize, string> = {
  md: 'py-2 px-4 text-[13px]',
  sm: 'px-3 py-2 text-[11px]',
  touch: 'py-2 px-4 text-[13px] max-md:h-11',
};

export const Tab = forwardRef<HTMLButtonElement, TabProps>(
  ({ active = false, size = 'md', orientation = 'horizontal', icon: Icon, iconSize, className, children, ...props }, ref) => {
    const eixo = POR_EIXO[orientation];
    return (
      <Button
        ref={ref}
        variant="ghost"
        className={cn(
          BASE,
          POR_TAMANHO[size],
          orientation === 'vertical' && 'py-2.5 px-5',
          eixo.inativo,
          active && eixo.ativo,
          className
        )}
        {...props}
      >
        {Icon && <Icon size={iconSize ?? (orientation === 'vertical' ? 18 : 16)} />}
        {children}
      </Button>
    );
  }
);

Tab.displayName = 'Tab';
